import { createFileRoute } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { useAdminOrganizationDetail } from '@/features/admin/services/admin.convex'

const billingBadgeVariant: Record<
  string,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  active: 'default',
  unpaid: 'secondary',
  cancelled: 'outline',
  expired: 'destructive',
  past_due: 'destructive',
  paused: 'secondary',
}

export const Route = createFileRoute(
  '/(dashboard)/admin/organizations/$organizationId/billing/',
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    name: (search.name as string) || undefined,
  }),
  beforeLoad: ({ params, search }) => ({
    breadcrumbs: [
      { title: 'Admin' },
      { title: 'Organizations', href: '/admin/organizations' },
      { title: search.name || params.organizationId },
      { title: 'Billing' },
    ],
  }),
})

function RouteComponent() {
  const { organizationId } = Route.useParams()
  const { data: org } = useAdminOrganizationDetail(organizationId)

  if (!org) return null

  const BASE_FREE = 5

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="h-full rounded-lg bg-muted/30 py-6 px-6">
          <p className="text-sm font-medium text-muted-foreground">Status</p>
          <div className="mt-3">
            <Badge
              variant={billingBadgeVariant[org.billingStatus] ?? 'secondary'}
            >
              {org.billingStatus.replace('_', ' ')}
            </Badge>
          </div>
        </div>
        <div className="h-full rounded-lg bg-muted/30 py-6 px-6">
          <p className="text-sm font-medium text-muted-foreground">Seats</p>
          <h3 className="mt-2 text-2xl font-bold tracking-tight">
            {org.currentEmployeeCount} / {org.employeeLimit}
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            {BASE_FREE} free + {org.paidQuantity} paid
          </p>
        </div>
        {org.subscriptionEndsAt && (
          <div className="h-full rounded-lg bg-muted/30 py-6 px-6">
            <p className="text-sm font-medium text-muted-foreground">
              Subscription Ends
            </p>
            <h3 className="mt-2 text-lg font-semibold tracking-tight">
              {new Date(org.subscriptionEndsAt).toLocaleDateString()}
            </h3>
          </div>
        )}
        {org.pendingPaidQuantity !== null && (
          <div className="h-full rounded-lg bg-muted/30 py-6 px-6">
            <p className="text-sm font-medium text-muted-foreground">
              Pending Downgrade
            </p>
            <p className="mt-2 text-sm">
              → {org.pendingPaidQuantity} paid seats at next renewal
            </p>
          </div>
        )}
      </div>

      {(org.lemonSqueezySubscriptionId || org.lemonSqueezyCustomerId) && (
        <div className="p-6 bg-muted/30 rounded-lg space-y-2">
          <p className="text-sm font-medium text-foreground">
            LemonSqueezy IDs
          </p>
          {org.lemonSqueezySubscriptionId && (
            <p className="font-mono text-xs text-muted-foreground">
              Subscription: {org.lemonSqueezySubscriptionId}
            </p>
          )}
          {org.lemonSqueezyCustomerId && (
            <p className="font-mono text-xs text-muted-foreground">
              Customer: {org.lemonSqueezyCustomerId}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
